import { useEffect } from 'react'

import { useEditAlarm } from '../hooks/useEditAlarm'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import * as Sentry from '@sentry/nextjs'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Button, ColorScheme, Grid, GridItem } from '@hivewatch/dark'

import { ControlledSelect, ControlledTextInput } from '@/components/ui'

import { DarkEditDeviceSection } from '../../dark/DarkEditDeviceSection'
import { DarkEditDeviceSkeleton } from '../../dark/DarkEditDeviceSkeleton'
import { AlarmDataTypeI } from '../../types/types'
import { isPlaced } from '../../utils/utils'
import { getAlarmInputs } from '../utils/utils'

interface DarkEditAlarmFormIProps {
  colorScheme: ColorScheme
  deviceId: string
  onLoadingChange: (loading: boolean) => void
}

export const DarkEditAlarmForm = ({
  colorScheme,
  deviceId,
  onLoadingChange,
}: DarkEditAlarmFormIProps) => {
  const { t } = useTranslation()
  const { shouldEnableUpdateDevice } = usePermissions()
  const isEventPromotion = useFeatureFlag(FeatureFlag.useEventPromotionAlarms)
  const {
    alarmData,
    isLoading,
    isUpdateDeviceLoading,
    isUpdateAlarmLoading,
    floorsOptions,
    incidentTypesOptions,
    isIncidentTypesLoading,
    updateAlarm,
  } = useEditAlarm({ deviceId })
  const form = useForm<AlarmDataTypeI>({ mode: 'onChange' })
  const {
    handleSubmit,
    errors,
    control,
    setValue,
    reset,
    formState: { dirtyFields, isDirty },
  } = form
  const { showSuccess, showError } = useToasts()

  const onSubmit: SubmitHandler<AlarmDataTypeI> = async (values) => {
    const formData = {
      id: deviceId,
      name: values.name,
      floorId: values.location?.value,
    }
    const alarmInputData = getAlarmInputs(deviceId, values, dirtyFields)

    try {
      await updateAlarm(formData, alarmInputData, dirtyFields)
      showSuccess(t('devices:crud_alarm_update_success'))
      reset(values)
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          action: 'update',
          feature: 'edit_alarm',
          surface: 'dark_edit_device_modal',
        },
        extra: {
          deviceId,
          dirtyFields: Object.keys(dirtyFields),
          isAlarmTypeCleared: alarmInputData.alarmTypeId?.value === null,
        },
      })
      showError()
    }
  }

  useEffect(() => {
    if (isDirty || isLoading) return

    setValue('name', alarmData?.device?.name, { shouldValidate: true })
    setValue(
      'location',
      floorsOptions.find(
        (option) => option.value === alarmData?.device?.floor?.id
      ),
      { shouldValidate: true }
    )
    setValue(
      'alarmTypeId',
      incidentTypesOptions.find(
        (option) => option.value === alarmData?.device?.alarm?.alarmType?.id
      ) ?? null
    )
    setValue('externalId', alarmData?.device?.alarm?.externalId, {
      shouldValidate: false,
    })
  }, [
    alarmData,
    floorsOptions,
    incidentTypesOptions,
    isDirty,
    isLoading,
    setValue,
  ])

  useEffect(() => {
    onLoadingChange(isLoading)
  }, [isLoading, onLoadingChange])

  if (!alarmData || isLoading) return <DarkEditDeviceSkeleton />

  const isSaveLoading = isUpdateDeviceLoading || isUpdateAlarmLoading

  return (
    <DarkEditDeviceSection
      actions={
        <Button
          colorScheme={colorScheme}
          disabled={!shouldEnableUpdateDevice || !isDirty}
          isLoading={isSaveLoading}
          onClick={handleSubmit(onSubmit)}
        >
          {isSaveLoading ? t('common:saving') : t('common:save')}
        </Button>
      }
      colorScheme={colorScheme}
      description={t('devices:crud_configure_description')}
      title={t('devices:crud_details_connection_title')}
    >
      <Grid
        gap='16px'
        templateColumns={{
          base: '1fr',
          md: 'repeat(2, 1fr)',
          xl: 'repeat(4, 1fr)',
        }}
      >
        <GridItem>
          <ControlledTextInput
            colorScheme={colorScheme}
            control={control}
            data-testid='devicesPage_editAlarmModal_name'
            errors={errors}
            label={t('common:device_name')}
            name='name'
            placeholder={t('devices:crud_device_name_placeholder')}
            rules={{ required: t('devices:crud_device_name_required') }}
            width='100%'
          />
        </GridItem>
        <GridItem>
          <ControlledSelect
            colorScheme={colorScheme}
            control={control}
            data-testid='devicesPage_editAlarmModal_location'
            hasWarning={Boolean(errors.location)}
            isClearable
            isDisabled={isPlaced(alarmData.device)}
            label={t('devices:crud_device_location_label')}
            name='location'
            options={floorsOptions}
            placeholder={t('devices:crud_device_location_select')}
            rules={{ required: true }}
            warningText={t('devices:crud_device_location_placeholder')}
          />
        </GridItem>
        <GridItem>
          <ControlledTextInput
            colorScheme={colorScheme}
            control={control}
            data-testid='devicesPage_editAlarmModal_externalId'
            errors={errors}
            label={t('devices:crud_external_id_label')}
            name='externalId'
            placeholder={t('devices:crud_alarm_external_id_label')}
            width='100%'
          />
        </GridItem>
        {isEventPromotion && (
          <GridItem>
            <ControlledSelect
              colorScheme={colorScheme}
              control={control}
              data-testid='devicesPage_editAlarmModal_incidentType'
              isClearable
              isLoading={isIncidentTypesLoading}
              label={t('common:incident_type')}
              name='alarmTypeId'
              options={incidentTypesOptions}
              placeholder={t('devices:crud_incident_type_placeholder')}
            />
          </GridItem>
        )}
      </Grid>
    </DarkEditDeviceSection>
  )
}
